/**
 * Overlays sample-report metrics from `tenant-report-prescreen.html` and
 * `tenant-report-fullscreen.html` onto the vendored v2 fixtures.
 *
 * Personal Details / Households / Rental Status / Employment stay on the
 * /demo form overlay. Scores, flags, AI, debt, savings, rent range, listing
 * facts, and income/rent verification come from the HTML samples. Student
 * fixtures are skipped.
 */
import type { FamilyPredictions } from '#app/utils/application-detail-v2/combined-display'
import type { CombinedReportData } from '#app/utils/application-detail-v2/combined-types'
import type {
	ApplicationDetailData,
	ParsedStepData,
} from '#app/utils/application-detail-v2/types'
import type { VerificationDataItem } from '#app/utils/report-data-helpers'
import {
	BAD_INCOME_TXNS,
	BAD_RENT_TXNS,
	GOOD_INCOME_TXNS,
	GOOD_RENT_TXNS,
	GUARANTOR_INCOME_TXNS,
	GUARANTOR_RENT_TXNS,
	type SampleTxn,
} from './overlay-html-verification'

type OverlayReport =
	| { kind: 'detail'; data: ApplicationDetailData }
	| { kind: 'combined'; data: CombinedReportData }

type SampleKind = 'prescreen' | 'good' | 'bad' | 'guarantor' | 'combined'

const SAMPLE_KIND: Partial<Record<string, SampleKind>> = {
	santos: 'prescreen',
	whitfield: 'good',
	malone: 'bad',
	'haddad-omar': 'good',
	'haddad-layla': 'bad',
	'haddad-karim': 'guarantor',
	'haddad-household': 'combined',
}

const LISTING = {
	title: '2100 Camilla Road',
	address: '2100 Camilla Road',
	bedrooms: 2,
	bathrooms: 1,
	squareFeet: 1000,
	unitNumber: '20',
	monthlyRent: 1328,
}

const MOVE_IN = {
	good: '2026-10-15',
	prescreen: '2026-10-15',
	combined: '2026-10-15',
	guarantor: '2026-10-15',
	bad: '2026-09-15',
} as const

const emptyLoan = (type: string) => ({
	type,
	used: '0%',
	limit: 0,
	balance: 0,
	pastDue: 0,
	paymentStatus: 'N/A',
	monthlyPayments: 0,
})

const flag = (level: 'High' | 'Medium', message: string) => ({
	level,
	message,
})

const GOOD_YELLOW = [
	flag(
		'Medium',
		'Rent payment timing appears irregular; verify with current landlord whether payments are agreed upon or consistently late.',
	),
	flag(
		'Medium',
		'Current employment less than 1 year. See details on application',
	),
	flag(
		'Medium',
		'No vehicle declared but recurring vehicle expenses are observed. Check with the applicant if parking is required.',
	),
	flag(
		'Medium',
		'No pet declared but recurring pet related transactions observed. Check with the applicant.',
	),
	flag('Medium', 'Savings are substantial to cover rent for at least 1 year'),
]

const BAD_RED = [
	flag(
		'High',
		"Applicant's declared income is not fully reflected in the linked bank account. Use the 'Request Additional Bank Account' link and advise the applicant to link the account where their income is deposited to complete income verification.",
	),
	flag('High', '7 NSF transactions found'),
	flag(
		'High',
		'The linked bank account does not reflect a substantial transaction history. Consider requesting that an additional bank account be linked for further review.',
	),
	flag('High', 'Evidence of borrowing from high-cost lending institutions.'),
	flag(
		'High',
		'A guarantor is recommended due to limited credit history, minimal tradeline depth, an elevated rent-to-income ratio, and unverified employment.',
	),
]

const BAD_YELLOW = [
	flag(
		'Medium',
		'Financial Stability: Limited credit history- see credit report',
	),
	flag('Medium', 'Applicant is renting for the first time'),
	flag(
		'Medium',
		'Applicant is not employed at the moment. See details on application',
	),
]

const GOOD_DEBT = {
	details: [
		{
			type: 'Revolving Credit',
			used: '19%',
			limit: 6500,
			balance: 1240,
			pastDue: 0,
			paymentStatus: 'Up to date',
			monthlyPayments: 45,
		},
		{
			type: 'Auto Loan',
			used: '35%',
			limit: 24000,
			balance: 8420,
			pastDue: 0,
			paymentStatus: 'Up to date',
			monthlyPayments: 389,
		},
		emptyLoan('Student Loan'),
	],
	totalDebtOwing: 9660,
	monthlyDebtPayment: 434,
}

const BAD_DEBT = {
	details: [
		{
			type: 'Revolving Credit',
			used: '73%',
			limit: 19595,
			balance: 14251,
			pastDue: 0,
			paymentStatus: 'Making regular payments under consolidation order',
			monthlyPayments: 20,
		},
		emptyLoan('Auto Loan'),
		emptyLoan('Student Loan'),
	],
	totalDebtOwing: 23407,
	monthlyDebtPayment: 237,
}

const GUARANTOR_DEBT = {
	details: [
		{
			type: 'Revolving Credit',
			used: '16%',
			limit: 13700,
			balance: 2124,
			pastDue: 0,
			paymentStatus: 'Up to date',
			monthlyPayments: 20,
		},
		emptyLoan('Auto Loan'),
		emptyLoan('Student Loan'),
	],
	totalDebtOwing: 2124,
	monthlyDebtPayment: 20,
}

const GOOD_AI =
	'John Carter works in factory production at Bosco And Roxys on a part-time basis, with a declared income of about $40,000 a year. They have a TrueTenant score of 81 out of 100 and an eligible monthly rent range of $1,661 to $1,931. They currently rent with Sifton, and rent payments were found in each of the last 11 months. Credit use is moderate, with revolving utilization around 19%, a car loan in good standing, and no collections, bankruptcy, or written-off accounts on file. The main concerns are that rent is often paid in several partial payments during the month and their current job began only 3 months ago. A guarantor is not recommended.'

const BAD_AI =
	'Sarah Carter is currently not employed. They have a TrueTenant score of 45 out of 100 and an eligible monthly rent range of $435 to $505. They are a first-time renter. They show some positive financial habits, including no debt burdens or major derogatory credit issues and very low credit use, which suggests a generally clean credit profile. The main concerns are that their income and employment are not well verified through the linked account, the account history appears limited, and they have a thin credit background as a first-time renter. A guarantor is recommended.'

const GUARANTOR_AI = `Robert Carter is currently employed as a priest father on a permanent basis, earning about $40,000 a year. They show a strong credit history, with low use of available credit, very modest existing debt, and no signs of collections, bankruptcy, or written-off accounts, and their verified income comfortably covers this rent. The main gaps are a limited savings buffer and no established mortgage or rent payment history to draw on, but based on this assessment they can be considered acceptable as a guarantor, subject to the landlord's final decision.`

const COMBINED_SUMMARY =
	'John Carter and Sarah Carter are applying together as a family household, with Robert Carter as their guarantor. The household has a combined TrueTenant score of 63 out of 100 and an eligible monthly rent range of $2,096 to $2,436, with combined savings of $48,257.'

function asRecord(value: unknown): Record<string, unknown> {
	if (value != null && typeof value === 'object' && !Array.isArray(value)) {
		return { ...(value as Record<string, unknown>) }
	}
	return {}
}

type SampleBank = {
	institution: string
	holderName: string
	connectedAt: string
}

type SampleVerification = {
	holder: string
	declaredRent: string | null
	income: SampleTxn[]
	rent: SampleTxn[]
	incomeStatus: string
	incomeDetails: string
	rentStatus: string
	rentDetails: string
	declaredIncome: Record<string, unknown>
	bank: SampleBank
}

const GOOD_VERIFICATION: SampleVerification = {
	holder: 'JOHN CARTER',
	declaredRent: '1560',
	income: GOOD_INCOME_TXNS,
	rent: GOOD_RENT_TXNS,
	incomeStatus: 'Completed',
	incomeDetails: 'Income deposits from the linked account',
	rentStatus: 'Completed',
	rentDetails: 'Rent verified for 11 months',
	declaredIncome: {
		declaredCurrent: { netMonthly: 2695, grossMonthly: 3333 },
		declaredPrevious: null,
	},
	bank: {
		institution: 'TD Canada Trust',
		holderName: 'JOHN C.',
		connectedAt: '2026-07-19T14:30:00.000Z',
	},
}

const BAD_VERIFICATION: SampleVerification = {
	holder: 'SARAH CARTER',
	declaredRent: '800',
	income: BAD_INCOME_TXNS,
	rent: BAD_RENT_TXNS,
	incomeStatus: 'Inconsistent',
	incomeDetails:
		"Applicant's declared income is not fully reflected in the linked bank account.",
	rentStatus: 'Not Found',
	rentDetails:
		'First-time renter — no rent payments were found in the linked account.',
	declaredIncome: {
		declaredCurrent: { netMonthly: null, grossMonthly: 3750 },
		declaredPrevious: null,
	},
	bank: {
		institution: 'TD',
		holderName: 'SARAH C.',
		connectedAt: '2026-09-10T22:45:00.000Z',
	},
}

const GUARANTOR_VERIFICATION: SampleVerification = {
	holder: 'ROBERT CARTER',
	declaredRent: null,
	income: GUARANTOR_INCOME_TXNS,
	rent: GUARANTOR_RENT_TXNS,
	incomeStatus: 'Completed',
	incomeDetails: 'Income deposits from the linked account',
	rentStatus: 'Completed',
	rentDetails: 'Possible rent-related transfers in the linked account',
	declaredIncome: {
		declaredCurrent: { netMonthly: 2695, grossMonthly: 3333 },
		declaredPrevious: null,
	},
	bank: {
		institution: 'BMO',
		holderName: 'ROBERT C.',
		connectedAt: '2026-09-09T16:48:00.000Z',
	},
}

function withHolder(rows: SampleTxn[], holder: string): VerificationDataItem[] {
	return rows.map((row) => ({
		...row,
		holder,
		currency: 'CAD',
	}))
}

function patchBank(
	step: ParsedStepData['verify-income'],
	bank: SampleBank,
): ParsedStepData['verify-income'] {
	const base = asRecord(step)
	const accounts = Array.isArray(base.accounts) ? [...base.accounts] : [{}]
	const first = asRecord(accounts[0])
	const summary = asRecord(first.summary)
	accounts[0] = {
		...first,
		status: 'success',
		addedAt: bank.connectedAt,
		verifiedAt: bank.connectedAt,
		isPrimary: true,
		summary: {
			...summary,
			institution: bank.institution,
			holderName: bank.holderName,
		},
	}
	return {
		...base,
		completed: true,
		bankVerified: true,
		accounts,
	} as ParsedStepData['verify-income']
}

function applySampleVerification(
	data: ApplicationDetailData,
	sample: SampleVerification,
	predictions: Record<string, unknown>,
) {
	const vs = asRecord(predictions.verificationSummary)
	predictions.verificationSummary = {
		...vs,
		incomeVerification: {
			...asRecord(vs.incomeVerification),
			status: sample.incomeStatus,
			details: sample.incomeDetails,
			data: withHolder(sample.income, sample.holder),
			declaredIncome: sample.declaredIncome,
		},
		rentVerification: {
			...asRecord(vs.rentVerification),
			status: sample.rentStatus,
			details: sample.rentDetails,
			data: withHolder(sample.rent, sample.holder),
		},
	}
	const rental = data.steps.rental
		? {
				...data.steps.rental,
				currentRent: sample.declaredRent ?? '',
			}
		: data.steps.rental
	data.steps = {
		...data.steps,
		rental,
		'verify-income': patchBank(data.steps['verify-income'], sample.bank),
	}
}

function applyListing(
	property: ApplicationDetailData['property'] | CombinedReportData['property'],
	step: ParsedStepData['property'] | undefined,
	moveInDate: string,
) {
	const nextProperty = { ...property, ...LISTING }
	const nextStep = step
		? {
				...step,
				unitNumber: LISTING.unitNumber,
				bedrooms: LISTING.bedrooms,
				bathrooms: LISTING.bathrooms,
				squareFeet: LISTING.squareFeet,
				monthlyRent: LISTING.monthlyRent,
				moveInDate,
			}
		: step
	return { property: nextProperty, step: nextStep }
}

function patchPredictions(
	data: ApplicationDetailData,
	score: number | null,
	guarantorRequired: boolean,
	update: (predictions: Record<string, unknown>) => void,
): ApplicationDetailData {
	const results = data.results
	const ai = asRecord(results?.aiReportData)
	const predictions = asRecord(ai.predictions)
	update(predictions)
	return {
		...data,
		application: {
			...data.application,
			score,
			guarantorRequired,
		},
		results: results
			? {
					...results,
					overallScore: score,
					guarantorRequired,
					aiReportData: { ...ai, predictions },
				}
			: results,
	}
}

function overlayGood(data: ApplicationDetailData): ApplicationDetailData {
	const listed = applyListing(data.property, data.steps.property, MOVE_IN.good)
	const next = {
		...data,
		property: listed.property,
		steps: { ...data.steps, property: listed.step },
	}
	return patchPredictions(next, 81, false, (p) => {
		applySampleVerification(next, GOOD_VERIFICATION, p)
		p.applicant = {
			...asRecord(p.applicant),
			name: 'JOHN CARTER',
			rentRange: { min: 1661, max: 1931, currency: 'CAD' },
			trueTenantScore: 81,
			guarantorScore: 0,
			guarantorRecommendation: 'Guarantor not Recommended',
		}
		p.riskFlags = { red: [], yellow: GOOD_YELLOW }
		p.financialSummary = {
			...asRecord(p.financialSummary),
			currency: 'CAD',
			savingsInvestments: 45000,
		}
		p.debtSummary = GOOD_DEBT
		p.collections = { data: [] }
		p.writtenOffAccounts = { data: [] }
		p.aiGeneratedSummary = GOOD_AI
	})
}

function overlayPrescreen(data: ApplicationDetailData): ApplicationDetailData {
	const listed = applyListing(
		data.property,
		data.steps.property,
		MOVE_IN.prescreen,
	)
	const next = {
		...data,
		property: listed.property,
		steps: { ...data.steps, property: listed.step },
	}
	return patchPredictions(next, null, false, (p) => {
		applySampleVerification(next, GOOD_VERIFICATION, p)
		p.applicant = {
			...asRecord(p.applicant),
			name: 'JOHN CARTER',
			rentRange: { min: 1661, max: 1931, currency: 'CAD' },
			trueTenantScore: null,
			guarantorScore: null,
			guarantorRecommendation: null,
		}
		p.riskFlags = { red: [], yellow: [] }
		p.financialSummary = {
			...asRecord(p.financialSummary),
			currency: 'CAD',
			savingsInvestments: 45000,
		}
		p.debtSummary = null
		p.aiGeneratedSummary = ''
	})
}

function overlayBad(data: ApplicationDetailData): ApplicationDetailData {
	const listed = applyListing(data.property, data.steps.property, MOVE_IN.bad)
	const next = {
		...data,
		property: listed.property,
		steps: { ...data.steps, property: listed.step },
	}
	return patchPredictions(next, 45, true, (p) => {
		applySampleVerification(next, BAD_VERIFICATION, p)
		p.applicant = {
			...asRecord(p.applicant),
			name: 'JOHN CARTER',
			rentRange: { min: 435, max: 505, currency: 'CAD' },
			trueTenantScore: 45,
			guarantorScore: 100,
			guarantorRecommendation: 'Guarantor Recommended',
		}
		p.riskFlags = { red: BAD_RED, yellow: BAD_YELLOW }
		p.financialSummary = {
			...asRecord(p.financialSummary),
			currency: 'CAD',
			savingsInvestments: 3257,
		}
		p.debtSummary = BAD_DEBT
		p.collections = {
			data: [
				{
					date: '01 Mar 2023',
					amount: 0,
					status: 'Paid',
					remarks: 'Paid Collection, Last payment: Sep, 2021',
					creditor: 'SEZZLE CANADA CORP',
					originalAmount: 550,
				},
			],
		}
		p.writtenOffAccounts = {
			data: [
				{
					date: 'Oct 2024',
					amount: 9156,
					status: 'Unpaid',
					remarks:
						'WRITTEN-OFF, AUTO, 60 days past due - 2024-12, 60 days past due - 2024-11; 30 days past due - 2024-10',
					creditor: 'Auto Financing',
					originalAmount: 0,
				},
				{
					date: 'Oct 2024',
					amount: 0,
					status: 'Paid',
					remarks:
						'WRITTEN-OFF, CREDIT COUNSELLING, Making repayments - 2026-02, 120+ days past due - 2026-01; 90 days past due - 2025-12',
					creditor: 'National Credit Card Companies',
					originalAmount: 2500,
				},
				{
					date: 'Jan 2022',
					amount: 0,
					status: 'Paid',
					remarks:
						'WRITTEN-OFF, Making repayments - 2022-12, Making repayments - 2022-11; Making repayments - 2022-10',
					creditor: 'Banks',
					originalAmount: 1527,
				},
			],
		}
		p.bankruptcyAndInsolvency = {
			data: [
				{
					assets: 48000,
					status: 'Unpaid',
					remarks: 'OFF SUP BKRPT, Proposal to creditors',
					trustee: '400118275 MSI SPE…',
					reported: '2021-12-01',
					liability: 99099,
				},
			],
		}
		p.aiGeneratedSummary = BAD_AI
	})
}

function overlayGuarantor(data: ApplicationDetailData): ApplicationDetailData {
	const listed = applyListing(
		data.property,
		data.steps.property,
		MOVE_IN.guarantor,
	)
	const next = {
		...data,
		property: listed.property,
		steps: { ...data.steps, property: listed.step },
	}
	return patchPredictions(next, null, false, (p) => {
		applySampleVerification(next, GUARANTOR_VERIFICATION, p)
		p.guarantor = {
			...asRecord(p.guarantor),
			name: 'ROBERT CARTER',
		}
		p.riskFlags = {
			red: [],
			yellow: [
				flag(
					'Medium',
					'Guarantor accepted based on income above the requirement and a strong credit history. Note: savings fall below the preferred level, offset by the strengths above.',
				),
			],
		}
		p.financialSummary = {
			...asRecord(p.financialSummary),
			currency: 'CAD',
			savingsInvestments: 10715,
		}
		p.debtSummary = GUARANTOR_DEBT
		p.collections = { data: [] }
		p.writtenOffAccounts = { data: [] }
		p.guarantorAccessibility = {
			...asRecord(p.guarantorAccessibility),
			status: 'Acceptable',
		}
		p.aiGeneratedSummary = GUARANTOR_AI
	})
}

function overlayCombined(data: CombinedReportData): CombinedReportData {
	const listedPrimary = applyListing(
		data.property,
		data.primaryPropertyStep,
		MOVE_IN.combined,
	)
	const listedHousehold = applyListing(
		listedPrimary.property,
		data.householdPropertyStep,
		MOVE_IN.combined,
	)
	const familyPredictions: FamilyPredictions = {
		familyScore: 63,
		rentRange: { min: 2096, max: 2436 },
		guarantorScore: 100,
		guarantorRecommendation: 'Guarantor Recommended',
		relationshipType: 'family',
		riskFlags: { red: [], yellow: [] },
		savingsInvestments: 48257,
		verificationSummary: {
			id: { status: 'Completed' },
			income: { status: 'Inconsistent' },
			rent: { status: 'Completed' },
		},
		summary: COMBINED_SUMMARY,
	}
	const api = asRecord(data.familyScore?.apiResponse)
	const predictions = Array.isArray(api.predictions)
		? [...api.predictions]
		: [{}]
	predictions[0] = {
		...asRecord(predictions[0]),
		rentRange: { min: 2096, max: 2436, currency: 'CAD' },
		riskFlags: { red: [], yellow: [], family_rent_check_passed: true },
		financialSummary: { currency: 'CAD', savingsInvestments: 48257 },
		trueTenantFamilyScore: 63,
		guarantorRecommendation: 'Guarantor Recommended',
		summary: COMBINED_SUMMARY,
	}
	return {
		...data,
		application: {
			...data.application,
			applicantName: 'John Carter',
			applicantEmail: 'john.carter@truetenant.ai',
			score: 63,
			guarantorRequired: true,
		},
		property: listedHousehold.property,
		primaryPropertyStep: listedPrimary.step,
		householdPropertyStep: listedHousehold.step,
		members: data.members.map((member) => {
			if (member.applicationId === 'demo-haddad-omar') {
				return {
					...member,
					displayName: 'John Carter',
					email: 'john.carter@truetenant.ai',
					score: 81,
					guarantorRequired: false,
					photoUrl: '/img/demo_applicant.jpg',
				}
			}
			if (member.applicationId === 'demo-haddad-layla') {
				return {
					...member,
					displayName: 'Sarah Carter',
					relationshipLabel: 'Spouse',
					email: 'sarah.carter@truetenant.ai',
					score: 45,
					guarantorRequired: true,
					photoUrl: '/img/demo_coapplicant.png',
				}
			}
			if (member.applicationId === 'demo-haddad-karim') {
				return {
					...member,
					displayName: 'Robert Carter',
					email: 'robert.carter@truetenant.ai',
					score: null,
					guarantorAcceptable: true,
					photoUrl: '/img/demo_guarantor.jpg',
				}
			}
			return member
		}),
		memberFlags: {
			...data.memberFlags,
			'demo-haddad-omar': {
				red: [],
				yellow: GOOD_YELLOW.map((entry) => entry.message),
			},
			'demo-haddad-layla': {
				red: BAD_RED.map((entry) => entry.message),
				yellow: BAD_YELLOW.map((entry) => entry.message),
			},
			'demo-haddad-karim': {
				red: [],
				yellow: [
					'Guarantor accepted based on income above the requirement and a strong credit history. Note: savings fall below the preferred level, offset by the strengths above.',
				],
			},
		},
		familyScore: data.familyScore
			? {
					...data.familyScore,
					score: 63,
					apiResponse: { ...api, predictions },
				}
			: data.familyScore,
		familyPredictions,
	}
}

function overlayDetail(
	data: ApplicationDetailData,
	kind: SampleKind,
): ApplicationDetailData {
	if (kind === 'prescreen') return overlayPrescreen(data)
	if (kind === 'good') return overlayGood(data)
	if (kind === 'bad') return overlayBad(data)
	if (kind === 'guarantor') return overlayGuarantor(data)
	return data
}

/** Apply HTML sample scores/flags/metrics. Student variants pass through. */
export function overlayHtmlSampleResults(
	report: OverlayReport,
	variant: string,
): OverlayReport {
	const kind = SAMPLE_KIND[variant]
	if (!kind) return report
	if (report.kind === 'combined') {
		return { kind: 'combined', data: overlayCombined(report.data) }
	}
	return { kind: 'detail', data: overlayDetail(report.data, kind) }
}
